/**
 * ProductService — unified product catalogue (favours + asoebi + decor + accessories + fabric).
 * Source of truth is the GuestGuru API (/products endpoint).
 * Reads fall back to the in-memory cache on API failure; writes surface it.
 */

import { apiId } from '../utils/apiId';
import { supplierPrice } from '../utils/supplierPrice';
import { z, ProductSchema, ProductSupplierSchema } from '@guestguru/shared';
import { Product, ProductType, ProductSupplier, StockMovement, StockMovementReason } from '../types/Product';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, planningPath } from './PlanningApiClient';

// ── PlanningProduct — raw wire type from /planning/:id/products ───────────────
// (id is a uuid string from the backend)

export type { ProductSupplier };

export interface PlanningProduct {
  id: string;
  project_id: string;
  /** Older servers only send this spelling. */
  wedding_id?: string;
  name: string;
  description: string | null;
  types: string[];
  suppliers: ProductSupplier[];
  stock_pools: Record<string, number>;
  image: string | null;
  customizable: boolean;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

// ── Public event ──────────────────────────────────────────────────────────────

export const PRODUCT_CHANGE_EVENT = 'anojeje_product_change';

// ── In-memory cache ───────────────────────────────────────────────────────────

let productCache: Product[] = [];

// ── API shapes from @guestguru/shared ────────────────────────────────────────
type ApiProductSupplier = z.infer<typeof ProductSupplierSchema>;
type ApiProduct = z.infer<typeof ProductSchema>;

// ── Serialisation helpers ─────────────────────────────────────────────────────

function transformApiSupplier(s: ApiProductSupplier): ProductSupplier {
  return {
    id: s.id,
    name: s.name,
    costNGN: s.costNGN,
    costUSD: s.costUSD,
    costCNY: s.costCNY,
    isPreferred: s.isPreferred,
    notes: s.notes,
  };
}

function transformApiProduct(row: ApiProduct): Product {
  const validTypes: ProductType[] = ['favour', 'asoebi', 'decor', 'accessories', 'internal', 'fabric'];
  const types = ((row.types || []) as string[]).filter((t): t is ProductType => validTypes.includes(t as ProductType));

  const stockPools: Partial<Record<ProductType, number>> = {};
  for (const [key, value] of Object.entries(row.stock_pools || {})) {
    if (validTypes.includes(key as ProductType)) {
      stockPools[key as ProductType] = value;
    }
  }

  return {
    id: String(row.id),
    name: row.name,
    types,
    suppliers: (row.suppliers || []).map(transformApiSupplier),
    stockPools,
    image: row.image
      ? (row.image.startsWith('http') ? row.image : `https://gg.ainaeco.uk${row.image}`)
      : undefined,
    description: row.description ?? undefined,
    // @guestguru/shared's ProductSchema hasn't been updated to include
    // `customizable` even though the ans-backend /products route and DB column
    // both already have it (route-level Zod schema there validates it
    // directly, not via the shared package) — read it off the raw row
    // rather than waiting on a shared-package version bump for one field.
    customizable: (row as unknown as { customizable?: boolean }).customizable,
    // stock_movements is a real DB column but, like `customizable` above,
    // isn't in @guestguru/shared's ProductSchema yet — read off the raw row.
    stock_movements: (row as unknown as { stock_movements?: StockMovement[] }).stock_movements ?? [],
    archived: row.archived,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function productToApiBody(
  data: Omit<Product, 'id' | 'created_at' | 'updated_at'>
): Record<string, unknown> {
  const suppliers: ApiProductSupplier[] | undefined = data.suppliers?.map((s) => ({
    id: s.id,
    name: s.name,
    // A redacted cost reads back as null; send it as absent (JSON drops
    // undefined) so a save never writes the nulls a blind caller received.
    cost_ngn: s.costNGN ?? undefined,
    cost_usd: s.costUSD ?? undefined,
    cost_cny: s.costCNY ?? undefined,
    is_preferred: s.isPreferred,
    notes: s.notes,
  }));

  const stockPools: Record<string, number> = {};
  for (const [key, value] of Object.entries(data.stockPools)) {
    if (value !== undefined) {
      stockPools[key] = value;
    }
  }

  return {
    name: data.name,
    types: data.types,
    // A financially blind caller sends no suppliers at all: the API refuses (403) any write carrying the key.
    ...(suppliers ? { suppliers } : {}),
    stock_pools: stockPools,
    image: data.image || null,
    description: data.description || null,
    customizable: data.customizable ?? false,
    archived: data.archived ?? false,
  };
}

function dispatchChange(): void {
  window.dispatchEvent(new Event(PRODUCT_CHANGE_EVENT));
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Return the current in-memory cache without a network call. */
export function getProducts(): Product[] {
  return productCache;
}

/**
 * Fetch all products from the API, populate the in-memory cache, and
 * dispatch a change event.
 *
 * On network error the existing cache is kept so the UI stays usable.
 */
/**
 * Fetch archived products directly from the API — does not touch the shared
 * cache. The backend's `?archived=true` is strictly binary (either only
 * active or only archived rows, never both — see
 * db/repositories/planning/products.ts's `WHERE archived = $2`), so getting
 * an "all" view means combining this with the regular cache client-side
 * rather than the backend returning everything unconditionally.
 *
 * Deliberately separate from `refreshProductsFromApi()` / `productCache`:
 * every other consumer of that cache (procurement, decor, bundle pickers) has
 * always meant "active items only" and must keep meaning that by default —
 * this exists only for the one page that needs to show archived items.
 */
export async function fetchArchivedProducts(): Promise<Product[]> {
  const response = await apiGet<{ products: ApiProduct[] }>(planningPath('/products?archived=true'));
  return (response.products || []).map(transformApiProduct);
}

export async function refreshProductsFromApi(): Promise<Product[]> {
  try {
    const response = await apiGet<{ products: ApiProduct[] }>(planningPath('/products'));
    const items = (response.products || [])
      .filter((p) => !p.archived)
      .map(transformApiProduct);
    productCache = items;
    dispatchChange();
    return productCache;
  } catch {
    // Keep the existing cache so the UI stays usable.
    return productCache;
  }
}

/**
 * Add a product to the cache optimistically, POST it, and replace the
 * temporary entry with the server's row. Resolves with the persisted product;
 * rejects — after rolling the optimistic entry back out — if the POST fails.
 */
export async function createProduct(
  data: Omit<Product, 'id' | 'created_at' | 'updated_at'>,
  onIdSwap?: (tempId: string, serverId: string) => void
): Promise<Product> {
  const now = new Date().toISOString();
  const tempId = `local_${Date.now()}`;
  const tempProduct: Product = { ...data, id: tempId, created_at: now, updated_at: now };

  productCache = [...productCache, tempProduct];
  dispatchChange();

  let created: ApiProduct;
  try {
    created = await apiPost<ApiProduct>(planningPath('/products'), productToApiBody(data));
  } catch (err) {
    productCache = productCache.filter((p) => p.id !== tempId);
    dispatchChange();
    throw err;
  }

  const product = transformApiProduct(created);
  productCache = productCache.map((p) => (p.id === tempId ? product : p));
  dispatchChange();
  onIdSwap?.(tempId, product.id);
  return product;
}

/**
 * Update a product in the cache optimistically and PUT it. Rejects — after
 * restoring the pre-patch entry — if the API call fails.
 */
export async function updateProduct(
  id: string,
  patch: Partial<Omit<Product, 'id'>>
): Promise<void> {
  const serverId = apiId(id);
  if (!serverId) {
    // Only a product whose POST never completed still carries a `local_…` id.
    // Refuse loudly: skipping the API silently would look like a save.
    throw new Error('This item was never saved to the server, so it cannot be updated.');
  }

  const original = productCache.find((p) => p.id === id);
  if (!original) throw new Error('This item is no longer loaded, so it cannot be updated.');

  productCache = productCache.map((p) =>
    p.id === id ? { ...p, ...patch, updated_at: new Date().toISOString() } : p
  );
  dispatchChange();

  const updated = productCache.find((p) => p.id === id)!;
  const { id: _id, created_at: _ca, updated_at: _ua, ...rest } = updated;
  try {
    await apiPut(planningPath(`/products/${serverId}`), productToApiBody(rest));
  } catch (err) {
    productCache = productCache.map((p) => (p.id === id ? original : p));
    dispatchChange();
    throw err;
  }
}

/**
 * Remove a product from the cache optimistically and DELETE (archive) it.
 * Rejects — after putting the product back where it was — if the API call fails.
 */
export async function archiveProduct(id: string): Promise<void> {
  const serverId = apiId(id);
  if (!serverId) {
    throw new Error('This item was never saved to the server, so it cannot be archived.');
  }

  const index = productCache.findIndex((p) => p.id === id);
  const removed = index >= 0 ? productCache[index] : undefined;
  productCache = productCache.filter((p) => p.id !== id);
  dispatchChange();

  try {
    await apiDelete(planningPath(`/products/${serverId}`));
  } catch (err) {
    if (removed) {
      // Reinsert at its original position so the list order is unchanged.
      const restored = [...productCache];
      restored.splice(index, 0, removed);
      productCache = restored;
      dispatchChange();
    }
    throw err;
  }
}

/** Return a single product by id, or undefined if not found. */
export function getProduct(id: string): Product | undefined {
  return productCache.find((p) => p.id === id);
}

/** Return all products that include the given type. */
export function filterProductsByType(type: ProductType): Product[] {
  return productCache.filter((p) => p.types.includes(type));
}

/**
 * Adjust the stock pool quantity for a product and record why, via the
 * dedicated PATCH /products/:id/stock endpoint — the pool total and the
 * movement history are updated atomically server-side (see
 * updateStockPool in ans-backend's products repository), rather than a
 * client-side recompute-then-PUT of the whole product.
 * `delta` is positive for incoming stock (received) or negative for
 * outgoing (sold/distributed). Clamps to 0 — stock cannot go below zero.
 */
export async function recordStockMovement(
  productId: string,
  type: ProductType,
  delta: number,
  reason: StockMovementReason,
  note?: string
): Promise<Product | null> {
  const serverId = apiId(productId);
  if (!serverId || delta === 0) return null;

  try {
    const updated = await apiPatch<ApiProduct>(planningPath(`/products/${serverId}/stock`), {
      type,
      delta,
      reason,
      note: note || undefined,
    });
    const product = transformApiProduct(updated);
    productCache = productCache.map((p) => (p.id === productId ? product : p));
    dispatchChange();
    return product;
  } catch {
    return null;
  }
}

// ── PlanningProduct helpers ────────────────────────────────────────────────

/**
 * Return the preferred supplier for a PlanningProduct.
 * Falls back to the first supplier if none is marked preferred.
 */
export function getPreferredSupplier(
  product: PlanningProduct
): ProductSupplier | undefined {
  if (!product.suppliers || product.suppliers.length === 0) return undefined;
  return product.suppliers.find((s) => s.isPreferred) ?? product.suppliers[0];
}

/**
 * Return the price from the preferred supplier in the requested currency.
 * Falls back to 0 when cost data is missing.
 */
export function getProductPrice(
  product: PlanningProduct,
  currency: string
): number {
  const supplier = getPreferredSupplier(product);
  if (!supplier) return 0;
  return supplierPrice(supplier, currency) ?? 0;
}
